import { useEffect, useRef, useState } from 'react'
import { TAuthUser, TProfileLevel, TUserProfile } from '../../../services/resumes/types'
import { getProfileLevel, getUserProfile } from '../../../services/resumes'
import { EGender, EMaritalStatus } from '../../../UploadResumeModal/type'
import { ELanguage } from '../../../Utils/constants'
import ModelError from '../../../services/model'
import { TPostHog } from '../../types'
import { detectEncryptAccessToken } from '../../../Utils/helpers'

type TResponsePromise = [Partial<TUserProfile> | null, TProfileLevel | null]
export const DEFAULT_INTERVAL_CHECK_IDENTIFY = 50

const sendHogIdentify = async (postHog: TPostHog, user: Partial<TAuthUser> | null, baseMsUrl: string) => {
    if (!user || !user?.userId || !user?.accessToken) {
        return
    }
    if (!postHog) {
        return
    }
    try {
        const userProfilePromise = getUserProfile(detectEncryptAccessToken(user?.accessToken), baseMsUrl)
        const userLevelPromise = getProfileLevel(detectEncryptAccessToken(user?.accessToken), ELanguage.VI, baseMsUrl)

        const promiseResponse = await Promise.allSettled([userProfilePromise, userLevelPromise])

        const response: TResponsePromise = [null, null]
        promiseResponse.forEach((result: any) => {
            if (result.status === 'rejected') {
                response.push(null)
                return
            }
            // fulfilled and no Model
            if (result.status === 'fulfilled' && (result?.value instanceof ModelError || !result?.value)) {
                response.push(null)
                return
            }

            response.push(result.value)
        })

        const userProfile = response[0]
        const userLevel = response[1]
        // Marital
        let maritalStatus = 'Empty'
        if (userProfile?.maritalStatusId === EMaritalStatus.SINGLE) {
            maritalStatus = 'Single'
        } else if (userProfile?.maritalStatusId === EMaritalStatus.MARRIED) {
            maritalStatus = 'Married'
        }

        postHog?.identify(user?.userId.toString(), {
            is_jobseeker: true,
            user_id: user?.userId,
            resume_id: user?.resumeId,
            job_title: user?.jobTitle,
            job_level: user?.jobLevelId,
            years_experience: user?.yearExperience,
            location_city: user?.locationList,
            current_jobfunction_id: userProfile?.currentJobFunction,
            current_industry_id: userProfile?.currentIndustries,
            highest_degree_id: userProfile?.highestDegreeId ?? '',
            gender: userProfile?.genderId === EGender.MALE ? 'Male' : 'Female',
            marrigal_status: maritalStatus,
            expected_job_level_id: userProfile?.jobLevelId,
            is_searchable: !!userProfile?.isSearchable,
            wowcv_template_id: userProfile?.wowcvTemplateId,
            profile_strength: userLevel?.attributes?.profileStrength,
            search_setting_id: user?.searchSettingSortType
        })
    } catch (ex) {
        console.error('PostHog Error: ', ex)
    }
}
export { sendHogIdentify }

type TUseIdentifyOptions = {
    intervalTime?: number
}

/**
 * This useIdentify function is used to identify a user in PostHog.
 * @param user TAuthUser
 */
const useIdentify = (user: Partial<TAuthUser> | null, baseMsUrl: string, options?: TUseIdentifyOptions) => {
    // Config
    const intervalTime = options?.intervalTime ?? DEFAULT_INTERVAL_CHECK_IDENTIFY

    const [isIdentified, setIsIdentified] = useState<boolean>(false)
    const [isEnable, setIsEnable] = useState<boolean>(true)
    const [isSentIdentify, setIsSentIdentify] = useState<boolean>(false)
    const intervalIdRef = useRef<NodeJS.Timeout | null>(null)

    const clearAllInterval = () => {
        intervalIdRef.current && clearInterval(intervalIdRef.current)
        intervalIdRef.current = null
    }

    const checkingIdentified = () => {
        if (!window.posthog || !window.posthog._isIdentified || typeof window.posthog._isIdentified !== 'function') {
            return
        }
        const isIdentifiedValue = window?.posthog?._isIdentified()
        // Response value is not a boolean
        if (typeof isIdentifiedValue !== 'boolean') {
            return
        }
        // Already identified
        if (isIdentifiedValue) {
            setIsIdentified(true)
            // Disable interval
            setIsEnable(false)
            clearAllInterval()
            return
        }
        // Send identify
        if (!isSentIdentify) {
            sendHogIdentify(window?.posthog, user, baseMsUrl)
            setIsSentIdentify(true)
        }
    }

    useEffect(() => {
        if (!isEnable) {
            clearAllInterval()
            return
        }
        clearAllInterval()
        intervalIdRef.current = setInterval(checkingIdentified, intervalTime)

        return () => clearAllInterval()
    }, [isEnable, isSentIdentify, user])

    return {
        isIdentified
    }
}

export default useIdentify
